import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormationData } from '../../../shared/dto/EntityRepository';
import { DisplaySize, PEInfoType } from '../../../shared/enum/EnumRepository';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import FormationSlot from './FormationSlot';
import FormationInfoStack from './FormationInfoStack';
import {
  getFormationVisibleImageBottom,
  getFormationVisibleImageRight,
} from '../../../shared/util/formationUtils';
import {
  FORMATION_GRID_GAP_PERCENT,
  getFormationContentWidthRatio,
  getFormationGridAspectRatio,
  getFormationGridStyle,
  getFormationSlotPlacement,
} from './formationGrid';

export interface FormationGridViewProps {
  peId: string;
  formation: FormationData;
  displaySize?: DisplaySize;
  infoType?: PEInfoType;
  isMobile?: boolean;
}

const FormationGridView: React.FC<FormationGridViewProps> = ({
  peId,
  formation,
  displaySize = DisplaySize.BIG,
  infoType = PEInfoType.INLINE,
  isMobile = false,
}) => {
  const { isEditMode } = useEditMode();
  const gridRef = useRef<HTMLDivElement>(null);
  const [infoOffsetY, setInfoOffsetY] = useState(0);
  const [infoRightInsetPx, setInfoRightInsetPx] = useState<number | null>(null);
  const sortedItems = useMemo(
    () => [...formation.items].sort((a, b) => a.slotIndex - b.slotIndex),
    [formation.items],
  );

  const gridStyle = getFormationGridStyle(formation.formationType);
  const contentWidthRatio = getFormationContentWidthRatio(displaySize, isMobile);
  const infoRightInsetRatio = displaySize === DisplaySize.WIDE
    ? (1 - getFormationContentWidthRatio(DisplaySize.BIG, isMobile)) / 2
    : 0;
  const aspectRatio = getFormationGridAspectRatio(formation.formationType);

  useEffect(() => {
    if (isEditMode) {
      setInfoOffsetY(0);
      setInfoRightInsetPx(null);
      return undefined;
    }

    const grid = gridRef.current;
    if (!grid) return undefined;

    const updateInfoOffset = () => {
      const gridRect = grid.getBoundingClientRect();
      let lowestImageBottom: number | null = null;
      let rightmostImageRight: number | null = null;

      sortedItems.forEach((item) => {
        const slot = grid.querySelector<HTMLElement>(
          `[data-formation-slot-index="${item.slotIndex}"]`,
        );
        if (!slot) return;

        const slotRect = slot.getBoundingClientRect();
        const imageBottom = getFormationVisibleImageBottom(
          item,
          slotRect.top - gridRect.top,
          slotRect.width,
          slotRect.height,
        );
        if (imageBottom !== null) {
          lowestImageBottom = lowestImageBottom === null
            ? imageBottom
            : Math.max(lowestImageBottom, imageBottom);
        }

        const imageRight = getFormationVisibleImageRight(
          item,
          slotRect.left - gridRect.left,
          slotRect.width,
          slotRect.height,
        );
        if (imageRight !== null) {
          rightmostImageRight = rightmostImageRight === null
            ? imageRight
            : Math.max(rightmostImageRight, imageRight);
        }
      });

      const nextOffset = lowestImageBottom === null
        ? 0
        : Math.min(0, lowestImageBottom - gridRect.height);
      setInfoOffsetY((previousOffset) => (
        Math.abs(previousOffset - nextOffset) < 0.5 ? previousOffset : nextOffset
      ));

      const maximumInfoRight = gridRect.width * (1 - infoRightInsetRatio);
      const nextRightInset = rightmostImageRight === null
        ? gridRect.width - maximumInfoRight
        : gridRect.width - Math.min(maximumInfoRight, rightmostImageRight);
      setInfoRightInsetPx((previousInset) => (
        previousInset !== null && Math.abs(previousInset - nextRightInset) < 0.5
          ? previousInset
          : nextRightInset
      ));
    };

    updateInfoOffset();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateInfoOffset);
      return () => window.removeEventListener('resize', updateInfoOffset);
    }

    const resizeObserver = new ResizeObserver(updateInfoOffset);
    resizeObserver.observe(grid);
    grid.querySelectorAll<HTMLElement>('[data-formation-slot-index]')
      .forEach((slot) => resizeObserver.observe(slot));
    return () => resizeObserver.disconnect();
  }, [formation.formationType, infoRightInsetRatio, isEditMode, sortedItems]);

  return (
    <FormationGridShell $contentWidthRatio={contentWidthRatio}>
      <FormationGrid
        ref={gridRef}
        $gridTemplateColumns={gridStyle.gridTemplateColumns}
        $gridTemplateRows={gridStyle.gridTemplateRows}
        $aspectWidth={aspectRatio.width}
        $aspectHeight={aspectRatio.height}
      >
        {sortedItems.map((item) => (
          <FormationSlot
            key={item.id}
            peId={peId}
            item={item}
            placement={getFormationSlotPlacement(item.slotIndex, formation.formationType)}
            isEditMode={isEditMode}
          />
        ))}
      </FormationGrid>
      {infoType !== PEInfoType.NONE && (
        <FormationInfoStack
          peId={peId}
          items={sortedItems}
          isEditMode={isEditMode}
          offsetY={isEditMode ? 0 : infoOffsetY}
          rightInsetRatio={infoRightInsetRatio}
          rightInsetPx={isEditMode ? undefined : infoRightInsetPx ?? undefined}
        />
      )}
    </FormationGridShell>
  );
};

const FormationGridShell = styled.div<{ $contentWidthRatio: number }>`
  width: ${({ $contentWidthRatio }) => `${$contentWidthRatio * 100}%`};
  min-width: 0;
  margin: 0 auto;
  container-type: inline-size;
  transition: width 0.35s ease-in-out;
`;

const FormationGrid = styled.div<{
  $gridTemplateColumns: string;
  $gridTemplateRows: string;
  $aspectWidth: number;
  $aspectHeight: number;
}>`
  display: grid;
  width: 100%;
  min-width: 0;
  aspect-ratio: ${({ $aspectWidth, $aspectHeight }) => `${$aspectWidth} / ${$aspectHeight}`};
  min-height: 0;
  column-gap: ${FORMATION_GRID_GAP_PERCENT}cqw;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  grid-template-rows: ${({ $gridTemplateRows }) => $gridTemplateRows};
  align-items: stretch;
`;

export default FormationGridView;
